import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SectionLabel } from '@/components/ui/section-label';
import { cn } from '@/lib/utils';
import {
  assumptionDimensions,
  deriveRiskLevel,
  isImpossibleBlocker,
  type Certainty,
  type Importance,
  type RiskLevel,
} from '@/lib/assumption-risk';
import type { NodeMetadata } from '@/services/canvas-mappers';
import type { Node as ReactFlowNode } from '@xyflow/react';

const RISK_LABEL: Record<RiskLevel, string> = {
  critical: 'Critical',
  risky: 'Risky',
  fine: 'Fine',
};

const RISK_BADGE: Record<RiskLevel, string> = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  risky: 'bg-amber-100 text-amber-800 border-amber-200',
  fine: 'bg-green-100 text-green-800 border-green-200',
};

// Every element type this panel actually uses (buttons, the reason inputs).
// `:not(:disabled)` excludes controls the browser has already taken out of
// the tab order, so the trap below never wraps focus onto a dead button.
const FOCUSABLE_SELECTOR =
  'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

// What the sticky hides: the full claim, the model's reasoning, the decision
// record, and the two classification controls. The controls duplicate the drag
// gesture on purpose — drag is mouse-only, and this is the path that works with
// a keyboard or on a touch screen.
export function AssumptionDetail({
  node,
  canEdit,
  busy,
  onClose,
  onReclassify,
  onPatchMeta,
  onDelete,
  prompt,
}: {
  node: ReactFlowNode;
  canEdit: boolean;
  busy: boolean;
  onClose: () => void;
  onReclassify: (nodeId: string, importance: Importance, certainty: Certainty) => void;
  onPatchMeta: (nodeId: string, metadata: Record<string, unknown>, failMsg: string) => Promise<boolean>;
  onDelete: (nodeId: string) => Promise<boolean>;
  // The downgrade prompt, already built by AssumptionBoard when the panel is
  // its home (see the placement decision there) — this panel just places it,
  // it never decides whether it owns it.
  prompt?: ReactNode;
}) {
  const titleId = useId();
  const data = node.data as { label?: string; content?: string; metadata?: NodeMetadata } | undefined;
  const meta = data?.metadata;
  const { importance, certainty } = assumptionDimensions(meta);
  const risk = deriveRiskLevel(importance, certainty);
  const flagged = meta?.aiFlagged === 'impossible';
  const dismissed = meta?.impossibleDismissed === true;
  const blocking = isImpossibleBlocker(meta);

  const [dismissing, setDismissing] = useState(false);
  const [reason, setReason] = useState('');
  // Reopening the strip starts from a clean draft.
  useEffect(() => {
    if (!dismissing) setReason('');
  }, [dismissing]);

  // The panel is the keyboard route to reclassification, so it needs to behave
  // like one: focus lands inside it on open (the close button) rather than
  // staying wherever it was on the board.
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeButtonRef.current?.focus();
  }, [node.id]);

  const panelRef = useRef<HTMLDivElement>(null);

  // Whoever had focus when the panel opened, so closing it can put a keyboard
  // user back where they left off rather than dropping them on <body>.
  // Captured once per mount, not per node.id: a reclassify keeps this same
  // component instance mounted while its data changes, and re-capturing then
  // would grab an element already inside the panel instead of the opening
  // sticky.
  const openerRef = useRef<Element | null>(null);
  useEffect(() => {
    openerRef.current = document.activeElement;
    return () => {
      const opener = openerRef.current;
      // The opener may not exist by the time this runs: the PM may have
      // deleted this assumption, or a reclassify moved its sticky into a
      // different cell, which unmounts and remounts it elsewhere in the DOM.
      // Only restore focus to something still on the page; otherwise leave
      // it wherever the close/delete action already put it rather than
      // guessing.
      if (opener instanceof HTMLElement && opener.isConnected) {
        opener.focus();
      }
    };
  }, []);

  // Keeps Tab and Shift+Tab cycling inside the panel instead of walking out
  // into the page behind the backdrop, which is what aria-modal="true" claims
  // is already true. Focusable elements are queried fresh on every Tab press
  // rather than cached at mount, because the panel's own content changes
  // while it's open (buttons disable during a mutation, the dismiss-reason
  // strip mounts and unmounts) and a cached list would go stale immediately.
  const handlePanelKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (e.shiftKey) {
      if (active === first || !panel.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last || !panel.contains(active)) {
      e.preventDefault();
      first.focus();
    }
  };

  // A document-level listener, not one on the backdrop, because focus doesn't
  // reliably stay inside the panel: cancelling the dismiss-reason strip
  // unmounts its focused input, and browsers then move focus to <body>, which
  // sits outside the panel's own DOM subtree. A handler on the backdrop would
  // miss that keypress; document catches it regardless of where focus lands.
  // The reason input still stops the event from getting here at all when it
  // wants to swallow its own Escape.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const confirmDismiss = () => {
    if (busy) return;
    const trimmed = reason.trim();
    // Close only on success: a failed PUT keeps the typed reason on screen.
    void onPatchMeta(
      node.id,
      { ...(meta ?? {}), impossibleDismissed: true, ...(trimmed ? { dismissReason: trimmed } : {}) },
      'Dismiss failed',
    ).then((ok) => {
      if (ok) setDismissing(false);
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="mx-4 flex max-h-full w-full max-w-md flex-col rounded-lg border bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handlePanelKeyDown}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b p-4">
          <div className="min-w-0 space-y-2">
            <h2 id={titleId} className="text-sm font-semibold leading-snug">{data?.label || 'Untitled assumption'}</h2>
            <div className="flex flex-wrap items-center gap-1.5">
              {/* While the flag stands, the derived level governs nothing (step 7
                  treats the assumption as fragile regardless of it), so showing
                  it beside "Impossible" would just repeat the bug this fixes in
                  miniature. It returns once the flag is dismissed. */}
              {!blocking && (
                <span
                  className={cn(
                    'rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                    RISK_BADGE[risk],
                  )}
                >
                  {RISK_LABEL[risk]}
                </span>
              )}
              {blocking && (
                <span className="inline-flex items-center gap-1 rounded-sm border border-destructive/30 bg-destructive/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-destructive">
                  <AlertTriangle className="h-3 w-3" />
                  Impossible
                </span>
              )}
              {flagged && dismissed && (
                <span className="rounded-sm border border-muted-foreground/20 bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Impossible · dismissed
                </span>
              )}
            </div>
          </div>
          <Button
            ref={closeButtonRef}
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {data?.content && <p className="whitespace-pre-wrap text-xs text-muted-foreground">{data.content}</p>}
          {meta?.aiReasoning && (
            <div className="space-y-1">
              <SectionLabel>Why it was flagged this way</SectionLabel>
              <p className="whitespace-pre-wrap text-xs italic text-muted-foreground/80">{meta.aiReasoning}</p>
            </div>
          )}

          <div className="space-y-2">
            <SectionLabel>Where it sits</SectionLabel>
            <div className="flex flex-wrap gap-4">
              <Choice
                label="Importance"
                options={[
                  { value: 'high', label: 'Important' },
                  { value: 'low', label: 'Less important' },
                ]}
                value={importance}
                disabled={!canEdit || busy}
                onChange={(next) => onReclassify(node.id, next as Importance, certainty)}
              />
              <Choice
                label="Certainty"
                options={[
                  { value: 'known', label: 'We know' },
                  { value: 'unknown', label: "We don't know" },
                ]}
                value={certainty}
                disabled={!canEdit || busy}
                onChange={(next) => onReclassify(node.id, importance, next as Certainty)}
              />
            </div>
          </div>

          {prompt}

          {typeof meta?.dismissReason === 'string' && meta.dismissReason && dismissed && (
            <p className="text-xs italic text-muted-foreground">Dismissed: &ldquo;{meta.dismissReason}&rdquo;</p>
          )}
          {typeof meta?.downgradeReason === 'string' && meta.downgradeReason && risk !== 'critical' && (
            <p className="text-xs italic text-muted-foreground">
              No longer critical: &ldquo;{meta.downgradeReason}&rdquo;
            </p>
          )}

          {flagged && canEdit && !dismissing && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => {
                if (dismissed) {
                  void onPatchMeta(node.id, { ...(meta ?? {}), impossibleDismissed: false }, 'Restore failed');
                } else {
                  setDismissing(true);
                }
              }}
            >
              {dismissed ? 'Restore the flag' : 'Dismiss the flag'}
            </Button>
          )}
          {flagged && canEdit && dismissing && (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 500))}
                placeholder="Why do you disagree? (optional)"
                // A placeholder is not a dependable accessible name: it is the
                // last fallback in the name calculation and it vanishes as soon
                // as there is a value, so name the field explicitly with the
                // same wording sighted users see.
                aria-label="Why do you disagree? (optional)"
                className="min-w-0 flex-1 rounded-md border bg-background px-2 py-1 text-xs focus:outline-hidden focus:ring-1 focus:ring-ring"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') confirmDismiss();
                  // Stop propagation so this only cancels the reason strip. Left
                  // unstopped, the same keypress would also reach the panel's
                  // document-level Escape listener and close the whole dialog.
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    setDismissing(false);
                  }
                }}
              />
              <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={confirmDismiss} disabled={busy}>
                Dismiss
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-[11px]"
                onClick={() => setDismissing(false)}
                disabled={busy}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>

        {canEdit && (
          <div className="flex shrink-0 justify-end border-t p-3">
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-destructive"
              disabled={busy}
              onClick={() => {
                void onDelete(node.id).then((ok) => {
                  if (ok) onClose();
                });
              }}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Remove
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// Two-option segmented control. Same write path as a drag, so a keyboard user
// gets the whole board.
function Choice({
  label,
  options,
  value,
  disabled,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-1">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <div className="flex rounded-md border p-0.5">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={option.value === value}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={cn(
              'rounded px-2 py-1 text-[11px] transition-colors',
              option.value === value ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground hover:bg-muted/60',
              disabled && 'opacity-50',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}
